import React, { useState, useRef, useMemo, KeyboardEvent } from 'react';
import { InputText } from 'primereact/inputtext';
import { useMask } from '@react-input/mask';
import { ReactComponent as DownArrowIcon } from '../../assets/icons/svg/shared/ic-shared-down-arrow.svg';
import { ReactComponent as ClearIcon } from '../../assets/icons/svg/shared/ic-shared-clear.svg';
import { SelectMenu } from './components/SelectMenu';
import { MenuItem, LiliComboboxWithInputValueConstraint } from './components/SelectMenuItem';

interface LiliComboboxWithInputProps<T extends LiliComboboxWithInputValueConstraint> {
  id: string;
  items: MenuItem<T>[];
  maskConfig?: {
    mask: string;
    replacement: {
      [key: string]: RegExp;
    };
  };
  selectedComboBoxValue: T;
  onItemSelected: (item: T) => void;
  inputValue: string;
  onInputValueChange: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  selectSearchPlaceholder: string;
  SelectSearchPrefixIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  onKeyDown?: (ev: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
}

export const LiliComboBoxWIthInput = <T extends LiliComboboxWithInputValueConstraint>({
  id,
  maskConfig,
  items,
  selectedComboBoxValue,
  onItemSelected,
  inputValue,
  onInputValueChange,
  placeholder,
  isDisabled,
  selectSearchPlaceholder,
  SelectSearchPrefixIcon,
  onKeyDown,
  onBlur,
}: LiliComboboxWithInputProps<T>) => {
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const elTriggerButton = useRef<HTMLButtonElement | null>(null);
  const inputRef = useMask(maskConfig);

  const selectedItem = useMemo(
    () => items.find(({ value }) => value === selectedComboBoxValue) || items[0],
    [selectedComboBoxValue],
  );

  return (
    <div className='w-full flex rounded-[8px] border border-bw04 relative h-[54px]'>
      <button
        ref={elTriggerButton}
        data-testid={`${id}-button-trigger`}
        className={`w-fit flex items-center justify-center flex-grow rounded-bl-[8px] rounded-tl-[8px] border-r border-bw03 cursor-default  ${!isDisabled ? `cursor-pointer hover:bg-bw03 ${isSelectOpen ? 'bg-bw03' : ''}` : ''}`}
        onClick={(event) => {
          event.stopPropagation();
          if (isDisabled) return;
          setIsSelectOpen((prevState) => !prevState);
        }}
        type='button'
      >
        <div className={`py-[9px] pl-[16px] pr-[16px] flex items-center gap-[4px] ${!isDisabled ? 'pr-[8px]' : ''}`}>
          <span className='text-[38px]'>{selectedItem.title}</span>
          {!isDisabled && (
            <DownArrowIcon className={`w-[24px] stroke-bw06 text-bw06 ${isSelectOpen ? 'rotate-180' : ''}`} />
          )}
        </div>
      </button>

      <div className='w-full flex items-center gap-[4px] pl-[12px] pr-[16px] py-[14px] hover:bg-bw00 rounded-br-[8px] rounded-tr-[8px]'>
        <InputText
          onBlur={onBlur}
          data-testid={id}
          ref={inputRef}
          onChange={(event) => onInputValueChange(event?.target?.value || '')}
          className='w-full outline-none shadow-none bg-transparent'
          unstyled
          value={inputValue}
          placeholder={placeholder}
          onKeyDown={onKeyDown}
        />
        {inputValue && (
          <button onClick={() => onInputValueChange('')} type='button'>
            <ClearIcon />
          </button>
        )}
      </div>
      {isSelectOpen && (
        <SelectMenu
          id={id}
          elTriggerButton={elTriggerButton}
          setIsSelectOpen={setIsSelectOpen}
          onItemSelected={(value) => {
            onInputValueChange('');
            onItemSelected(value);
          }}
          selectedValue={selectedComboBoxValue}
          items={items}
          inputPlaceholder={selectSearchPlaceholder}
          InputPrefixIcon={SelectSearchPrefixIcon}
        />
      )}
    </div>
  );
};
